import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";

// color design tokens export
export const tokens = (mode) => ({
  // Palette inspirée de l'image fournie (fond blanc, bleu, vert, accents vifs)
  grey: {
    100: "#f8f9fa",
    200: "#f1f3f4",
    300: "#e9ecef",
    400: "#dee2e6",
    500: "#adb5bd",
    600: "#6c757d",
    700: "#495057",
    800: "#343a40",
    900: "#212529",
  },
  primary: {
    100: "#ffffff",
    200: "#e3f0fa",
    300: "#b3daf6",
    400: "#3192e1",
    500: "#1976d2", // bleu vif (boutons, accents)
    600: "#145ea8",
    700: "#0d3a75",
    800: "#082246",
    900: "#02101e",
  },
  greenAccent: {
    100: "#e6f9f0",
    200: "#bdf3dd",
    300: "#73e6b1",
    400: "#33d18e",
    500: "#00b86b", // vert accent (logo)
    600: "#009c5c",
    700: "#007b47",
    800: "#005a32",
    900: "#00391d",
  },
  redAccent: {
    100: "#ffeaea",
    200: "#ffbdbd",
    300: "#ff9191",
    400: "#ff6565",
    500: "#ff3939",
    600: "#cc2e2e",
    700: "#992222",
    800: "#661717",
    900: "#330b0b",
  },
  blueAccent: {
    100: "#e3f0fa",
    200: "#b3daf6",
    300: "#7cc2f2",
    400: "#3192e1",
    500: "#1976d2",
    600: "#145ea8",
    700: "#0d3a75",
    800: "#082246",
    900: "#02101e",
  },
});

// mui theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: "light", // Forcer le mode clair
      primary: {
        main: colors.primary[500],
      },
      secondary: {
        main: colors.greenAccent[500],
      },
      neutral: {
        dark: colors.grey[700],
        main: colors.grey[500],
        light: colors.grey[100],
      },
      background: {
        default: colors.primary[100], // blanc
        paper: colors.primary[100],
      },
      text: {
        primary: colors.grey[900],
        secondary: colors.grey[700],
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            '::-webkit-scrollbar': {
              width: '8px',
            },
            '::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '::-webkit-scrollbar-thumb': {
              backgroundColor: colors.grey[500], // #dee2e6
              borderRadius: '10px',
              '&:hover': {
                backgroundColor: colors.grey[600], // #adb5bd
              },
            },
            '::-webkit-scrollbar-button:start:decrement, ::-webkit-scrollbar-button:end:increment': {
              display: 'block',
              height: '12px',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'contain',
              backgroundColor: 'transparent',
            },
            '::-webkit-scrollbar-button:start:decrement': {
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%236c757d' viewBox='0 0 16 16'%3E%3Cpath d='M8 4l-7 6h14L8 4z'/%3E%3C/svg%3E")`,
              backgroundPosition: 'center 4px',
            },
            '::-webkit-scrollbar-button:end:increment': {
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%236c757d' viewBox='0 0 16 16'%3E%3Cpath d='M8 12l-7-6h14L8 12z'/%3E%3C/svg%3E")`,
              backgroundPosition: 'center 6px',
            },
          },
        },
      },
    },
    typography: {
      fontFamily: ["Montserrat", "Arial", "sans-serif"].join(","), // police moderne, proche de l'image
      fontSize: 14,
      h1: {
        fontFamily: ["Montserrat", "Arial", "sans-serif"].join(","),
        fontSize: 40,
        fontWeight: 700,
      },
      h2: {
        fontFamily: ["Montserrat", "Arial", "sans-serif"].join(","),
        fontSize: 32,
        fontWeight: 600,
      },
      h3: {
        fontFamily: ["Montserrat", "Arial", "sans-serif"].join(","),
        fontSize: 24,
        fontWeight: 600,
      },
      h4: {
        fontFamily: ["Montserrat", "Arial", "sans-serif"].join(","),
        fontSize: 20,
        fontWeight: 500,
      },
      h5: {
        fontFamily: ["Montserrat", "Arial", "sans-serif"].join(","),
        fontSize: 16,
        fontWeight: 500,
      },
      h6: {
        fontFamily: ["Montserrat", "Arial", "sans-serif"].join(","),
        fontSize: 14,
        fontWeight: 500,
      },
      body1: {
        fontFamily: ["Montserrat", "Arial", "sans-serif"].join(","),
        fontSize: 14,
      },
      body2: {
        fontFamily: ["Montserrat", "Arial", "sans-serif"].join(","),
        fontSize: 13,
      },
      button: {
        fontFamily: ["Montserrat", "Arial", "sans-serif"].join(","),
        fontWeight: 600,
      },
    },
  };
};

// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

export const useMode = () => {
  const [mode, setMode] = useState("light");
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => setMode((prev) => (prev === "light" ? "light" : "light")), // Force toujours light
    }),
    []
  );
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};
